import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaFilePdf, FaFileExcel, FaPrint } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { useDispatch, useSelector } from 'react-redux';
import { setAuth } from '../Redux/authSlice'; // Adjust the import path as needed
import { RootState } from '../Redux/store'; // Adjust the import path as needed
import { translations } from '../utils/translations';

interface Sale {
  id: number;
  customer_name: string;
  products: {
    name: string;
    quantity: number;
  }[];
  total_amount: number;
  date_sold: string;
  status: string;
}

interface SalesData {
  period: string;
  total_sales: string; // total_sales is a string (formatted with currency)
  start_date: string;
  end_date: string;
  sales: Sale[];
}

const SalesReport = () => {
  const [salesData, setSalesData] = useState<SalesData | null>(null);
  const [period, setPeriod] = useState<string>('daily'); // Default to 'daily'
  const [userRole, setUserRole] = useState<string>(''); // State for user role
  const dispatch = useDispatch();

  const language = useSelector((state: RootState) => state.language.language);
  const t = translations[language as keyof typeof translations];

  const fetchSalesData = useCallback(async (period: string) => {
    try {
      const response = await axios.get(`https://api.sominnovations.xyz/Branches/num_Sales?period=${period}`);
      setSalesData(response.data);
    } catch (error) {
      console.error('Error fetching sales data:', error);
    }
  }, []);

  const getCookie = (name: string): string => {
    const cookieName = name + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.startsWith(cookieName)) {
        return cookie.substring(cookieName.length);
      }
    }
    return '';
  };

  const fetchData = useCallback(async () => {
    try {
      const token = getCookie('token');
      if (!token) {
        dispatch(setAuth(false));
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const { data } = await axios.get('https://api.sominnovations.xyz/users/user', {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setUserRole(data.user.role); // Set the user role
      dispatch(setAuth(true));
    } catch (e) {
      dispatch(setAuth(false));
    }
  }, [dispatch]);

  useEffect(() => {
    fetchData(); // Fetch user role on component mount
    fetchSalesData(period); // Fetch sales data based on the selected period
  }, [period, fetchData, fetchSalesData]);

  // Function to download PDF
  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.text(`Sales Report - ${salesData?.period}`, 14, 16);
    doc.autoTable({
      head: [['Customer', 'Product', 'Quantity', 'Total Amount (Ksh)', 'Date Sold', 'Status']],
      body: salesData?.sales.map((sale) => {
        const products = sale.products.map((product) => `${product.name} (${product.quantity})`).join(', ');
        return [
          sale.customer_name,
          products,
          sale.total_amount.toFixed(2),
          new Date(sale.date_sold).toLocaleDateString(),
          sale.status,
        ];
      }),
      startY: 20,
    });

    doc.save(`sales_report_${salesData?.period}.pdf`);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      salesData?.sales?.map((sale) => ({
        Customer: sale.customer_name,
        Products: sale.products.map((product) => `${product.name} (${product.quantity})`).join(', '),
        'Total Amount (Ksh)': sale.total_amount.toFixed(2),
        'Date Sold': new Date(sale.date_sold).toLocaleDateString(),
        Status: sale.status,
      })) || [] // Fallback to an empty array if salesData or sales is undefined
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Data');
    XLSX.writeFile(workbook, `sales_report_${salesData?.period}.xlsx`);
  };

  // Function to print the report
  const printReport = () => {
    const printContent = document.getElementById('printableArea');
    if (printContent) {
      const newWindow = window.open('', '_blank');
      newWindow?.document.write(printContent.innerHTML);
      newWindow?.document.close();
      newWindow?.print();
    }
  };

  // If the user is not an admin, do not render the component
  if (userRole !== 'admin') {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert" style={{ margin: "0 auto", }}>
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="row">
        <div className="col-md-10 offset-md-2">
          <div className="card shadow">
            <div className="card-header bg-primary text-white d-flex justify-content-between">
              <h4>{t.saleReport}</h4>
              <div>
                <button className="btn btn-light me-2" onClick={downloadPDF}>
                  <FaFilePdf /> PDF
                </button>
                <button className="btn btn-light me-2" onClick={exportToExcel}>
                  <FaFileExcel /> Excel
                </button>
                <button className="btn btn-light" onClick={printReport}>
                  <FaPrint /> {t.print}
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="form-group mb-3">
                <label>{t.period}:</label>
                <select
                  className="form-select"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  <option value="daily">{t.daily}</option>
                  <option value="weekly">{t.weekly}</option>
                  <option value="monthly">{t.monthly}</option>
                  <option value="yearly">{t.yearly}</option>
                </select>
              </div>
              {salesData ? (
                <>
                  <div className="sales-summary mb-4">
                    <ul className="list-group">
                      <li className="list-group-item">
                        <strong>{t.period}:</strong> {salesData.period}
                      </li>
                      <li className="list-group-item">
                        <strong>{t.totalSales}:</strong> {salesData.total_sales}
                      </li>
                      <li className="list-group-item">
                        <strong>{t.startDate}:</strong> {salesData.start_date}
                      </li>
                      <li className="list-group-item">
                        <strong>{t.endDate}:</strong> {salesData.end_date}
                      </li>
                    </ul>
                  </div>
                  <div className="sales-details" id="printableArea">
                    <h5>{t.salesDetails}</h5>
                    <table className="table table-striped table-hover table-bordered table-sm">
                      <thead>
                        <tr>
                          <th>{t.customers}</th>
                          <th>{t.product}</th>
                          <th>{t.totalSales} (Ksh)</th>
                          <th>{t.dateAdded}</th>
                          <th>{t.status}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesData.sales.map((sale) => (
                          <tr key={sale.id}>
                            <td>{sale.customer_name || 'Walk-in Customer'}</td>
                            <td>{sale.products.map((product) => product.name).join(', ')}</td>
                            <td>{sale.total_amount.toFixed(2)}</td>
                            <td>{new Date(sale.date_sold).toLocaleDateString()}</td>
                            <td>
                              <span className={`badge ${sale.status === 'fully paid' ? 'bg-success' : 'bg-warning'}`}>
                                {sale.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesReport;